<template>
  <Teleport to="body">
    <TransitionGroup name="overlay" tag="div" class="overlay-stack">
      <div v-for="overlay in overlays" :key="overlay.id" class="overlay-stack__item" :data-kind="overlay.kind">
        <component
          :is="overlay.component"
          v-bind="overlay.props"
          :overlayId="overlay.id"
          :overlayKind="overlay.kind"
          :overlayZIndex="overlayZIndex(overlay.kind)"
          @close="close(overlay.id, 'action')"
        />
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from "vue";
import { overlayZIndex, useOverlayStack } from "../../composables/useOverlayStack";

const { overlays, close, closeTop } = useOverlayStack();
const keydownOptions = { capture: true } as const;

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const didClose = closeTop();
  if (didClose) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

onMounted(() => {
  window.addEventListener("keydown", handleKeydown, keydownOptions);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeydown, keydownOptions);
});
</script>

<style scoped>
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity var(--dur-2) var(--ease-out);
}

.overlay-stack {
  position: fixed;
  inset: 0;
  z-index: 4200;
  pointer-events: none;
}

.overlay-stack__item {
  position: fixed;
  inset: 0;
  pointer-events: auto;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-active :deep(.overlay-frame__scrim),
.overlay-leave-active :deep(.overlay-frame__scrim),
.overlay-enter-active :deep(.ui-sheet__scrim),
.overlay-leave-active :deep(.ui-sheet__scrim) {
  transition: opacity var(--dur-2) var(--ease-out);
}

.overlay-enter-from :deep(.overlay-frame__scrim),
.overlay-leave-to :deep(.overlay-frame__scrim),
.overlay-enter-from :deep(.ui-sheet__scrim),
.overlay-leave-to :deep(.ui-sheet__scrim) {
  opacity: 0;
}

.overlay-enter-active :deep(.overlay-frame__panel),
.overlay-leave-active :deep(.overlay-frame__panel) {
  transition: transform var(--dur-3) var(--ease-out), opacity var(--dur-3) var(--ease-out);
  will-change: transform, opacity;
}

.overlay-enter-from :deep(.overlay-frame__panel),
.overlay-leave-to :deep(.overlay-frame__panel) {
  opacity: 0;
  transform: translateY(10px) scale(0.985);
}
</style>
